import type { Position } from "@/types/game.types"

/**
 * Calculate distance between two positions
 */
export function distance(p1: Position, p2: Position): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Move from current position toward target position by a given distance
 * Returns the new position (may be at or past the target if distance is large enough)
 */
export function moveToward(current: Position, target: Position, moveDistance: number): Position {
  const dist = distance(current, target)

  // If we're already at or past the target, return target
  if (dist <= moveDistance) {
    return { x: target.x, y: target.y }
  }

  // Calculate direction and move
  const dx = target.x - current.x
  const dy = target.y - current.y
  const ratio = moveDistance / dist

  return {
    x: current.x + dx * ratio,
    y: current.y + dy * ratio,
  }
}

/**
 * Calculate angle from one position to another (in radians)
 */
export function angleToward(from: Position, to: Position): number {
  return Math.atan2(to.y - from.y, to.x - from.x)
}

/**
 * Check if a position has reached a waypoint (within threshold)
 */
export function hasReachedWaypoint(current: Position, waypoint: Position, threshold = 2): boolean {
  return distance(current, waypoint) <= threshold
}

// Legacy exports for backward compatibility
export const moveTowards = moveToward
export const calculateAngle = angleToward
